import axios from 'axios';
import { API_BASE_URL } from '../api';
import { postGenerationJob } from './generationJob.api';

export const generateKit = async (productDetails, branding, contentPreferences) => {
  const payload = {
    name: productDetails.name,
    description: productDetails.description,
    targetAudience: productDetails.targetAudience,
    brandProfile: branding,
    productDetails: productDetails,
    contentPreferences: contentPreferences,
    scene: productDetails.scene,
    sceneImage: productDetails.sceneImage,
    modelImage: productDetails.modelImage,
    selectedModel: productDetails.model
  };

  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const res = await axios.post(`${API_BASE_URL}/kits/submit-request`, payload, {
    headers,
    timeout: 120000 // 2 minutes timeout for kit generation
  });
  return res.data.data;
};

export const updateKit = async (kitId, updateData) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const res = await axios.patch(`${API_BASE_URL}/kits/${kitId}`, updateData, {
    headers,
    timeout: 30000 // 30 seconds timeout for kit updates
  });
  return res.data.data;
};

// Fetch a single kit by ID
export const getKitById = async (kitId) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const res = await axios.get(`${API_BASE_URL}/kits/${kitId}`, { headers, timeout: 30000 });
  return res.data?.data?.kit || res.data?.data || res.data;
};

/** AI kits linked to a saved garment (inspirational design) id */
export const getKitsByInspirationalDesignId = async (designId) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
  const res = await axios.get(`${API_BASE_URL}/kits/by-inspirational-design/${designId}`, {
    headers,
    timeout: 60000
  });
  return res.data?.data?.kits || [];
};

// Save virtual try-on result as a kit
export const saveVirtualTryOnToKit = async (virtualTryOnData) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const kitData = {
    name: `Virtual Try-On - ${virtualTryOnData.productType} - ${new Date().toLocaleDateString()}`,
    productDetails: {
      name: virtualTryOnData.productType,
      description: `Virtual try-on result for ${virtualTryOnData.productType}`,
      image: virtualTryOnData.originalProductImage,
      targetAudience: 'General Audience'
    },
    contentPreferences: {
      selectedTypes: ['Virtual Try-On'],
      tone: 'Professional'
    },
    contentTypesIncluded: ['Virtual Try-On'],
    thumbnail: virtualTryOnData.generatedImageUrl,
    generatedImageUrl: virtualTryOnData.generatedImageUrl,
    status: 'generated',
    creditsUsed: {
      imageCredits: 1,
      videoCredits: 0
    },
    // Store additional virtual try-on specific data
    virtualTryOnData: {
      productCategory: virtualTryOnData.productCategory,
      productType: virtualTryOnData.productType,
      originalUserImage: virtualTryOnData.originalUserImage,
      originalProductImage: virtualTryOnData.originalProductImage,
      generatedImageUrl: virtualTryOnData.generatedImageUrl,
      modelUsed: virtualTryOnData.modelUsed,
      processingTime: virtualTryOnData.processingTime
    }
  };

  const res = await axios.post(`${API_BASE_URL}/kits`, kitData, {
    headers,
    timeout: 30000 // 30 seconds timeout for saving virtual try-on to kit
  });
  return res.data;
};

export const downloadKitVideo = async (videoUrl, filename) => {
  try {
    // Fetch the video file
    const response = await fetch(videoUrl);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Convert to blob
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading video:', error);
    throw error;
  }
};

// Generate color variant for a kit
export const generateColorVariant = async (kitId, colorCode) => {
  const payload = {
    colorCode
  };

  const res = await postGenerationJob(
    `${API_BASE_URL}/kits/${kitId}/generate-color-variant`,
    payload,
    { maxWaitMs: 8 * 60 * 1000 }
  );
  return res;
};

// Generate color variant for multiple kits (bulk operation)
export const generateColorVariantForMultipleKits = async (kitIds, colorCode, onProgress = null) => {
  console.log(`🎨 Starting bulk color variant generation for ${kitIds.length} kits with color: ${colorCode}`);

  // Create an array to track progress
  const progressTracker = {
    completed: 0,
    total: kitIds.length,
    results: [],
    errors: []
  };

  // Helper function to update progress
  const updateProgress = (kitId, status, result = null, error = null) => {
    if (status === 'completed' || status === 'error') {
      progressTracker.completed++;
    }
    
    if (result) {
      progressTracker.results.push(result);
    }
    
    if (error) {
      progressTracker.errors.push({ kitId, error });
    }
    
    if (onProgress) {
      onProgress({
        current: progressTracker.completed,
        total: progressTracker.total,
        kitId,
        status,
        result,
        error
      });
    }
  };

  // Create all API calls as promises
  const kitPromises = kitIds.map(async (kitId) => {
    try {
      // Update progress to show generation started
      updateProgress(kitId, 'generating');

      const payload = {
        colorCode
      };

      console.log(`🎨 Generating color variant for kit ${kitId} with color ${colorCode}`);
      
      const response = await postGenerationJob(
        `${API_BASE_URL}/kits/${kitId}/generate-color-variant`,
        payload,
        { maxWaitMs: 8 * 60 * 1000 }
      );

      const result = {
        kitId,
        data: response,
        success: true
      };

      // Update progress for success
      updateProgress(kitId, 'completed', result);
      
      return result;

    } catch (error) {
      console.error(`Error generating color variant for kit ${kitId}:`, error);
      
      const errorResult = {
        kitId,
        success: false,
        error: error.response?.data?.message || error.message || 'Unknown error'
      };

      // Update progress for error
      updateProgress(kitId, 'error', null, errorResult);
      
      return errorResult;
    }
  });

  // Wait for all promises to complete
  const results = await Promise.all(kitPromises);

  console.log(`🎨 Bulk color variant generation completed: ${progressTracker.completed}/${progressTracker.total} successful`);

  return {
    success: progressTracker.errors.length === 0,
    results: progressTracker.results,
    errors: progressTracker.errors,
    total: progressTracker.total,
    completed: progressTracker.completed
  };
};

// Get kits by fabric image URL
export const getKitsByFabricImage = async (fabricImageUrl) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const encodedUrl = encodeURIComponent(fabricImageUrl);
  const res = await axios.get(`${API_BASE_URL}/kits/by-fabric/${encodedUrl}`, {
    headers,
    timeout: 30000
  });
  
  return res.data?.data?.kits || [];
};

// Get kits by fabric image URL (public endpoint - no auth required)
export const getKitsByFabricImagePublic = async (fabricImageUrl) => {
  const headers = {
    'Content-Type': 'application/json'
  };

  const encodedUrl = encodeURIComponent(fabricImageUrl);
  const res = await axios.get(`${API_BASE_URL}/kits/public/by-fabric/${encodedUrl}`, {
    headers,
    timeout: 30000
  });
  
  return res.data?.data?.kits || [];
};

// Get all public kits (public endpoint - no auth required)
export const getAllPublicKits = async (page = 1, limit = 20, search = '', category = '', landingPage = false) => {
  const headers = {
    'Content-Type': 'application/json'
  };

  const params = { page, limit };
  if (search) params.search = search;
  if (category) params.category = category;
  if (landingPage) params.landingPage = 'true';

  const res = await axios.get(`${API_BASE_URL}/kits/public/all`, {
    headers,
    params,
    timeout: 30000
  });
  
  return res.data?.data || { kits: [], pagination: {} };
};

/** Public kits for a seller phone (source fabric must be that seller's own public product) */
export const getPublicKitsByPhone = async (phoneNumber, page = 1, limit = 20, search = '') => {
  const headers = {
    'Content-Type': 'application/json'
  };
  const slug = encodeURIComponent(String(phoneNumber || '').trim().toLowerCase());
  const params = { page, limit };
  if (search) params.search = search;
  const res = await axios.get(`${API_BASE_URL}/kits/public/by-phone/${slug}`, {
    headers,
    params,
    timeout: 30000
  });
  return res.data?.data || { kits: [], pagination: {} };
};

// Toggle showOnLandingPage flag for a kit (admin only)
export const toggleKitLandingPage = async (kitId, showOnLandingPage) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const res = await axios.patch(`${API_BASE_URL}/kits/${kitId}/toggle-landing-page`, { showOnLandingPage }, {
    headers,
    timeout: 30000
  });
  return res.data;
}; 